import { useEffect, useRef } from "react";

import {
  LoaderFunctionArgs,
  useLocation,
  useMatches,
  useParams,
  useSearchParams,
  redirect,
} from "react-router-dom";
import { PointBase } from "../point/point.model";

/**
 * Hook to get typesafe and properly casted params from the URL.
 * You can use this hook instead of `useParams` from React Router when you
 * need to access to url params that you know are defined based on the route
 * context, eg. if you are in a route like `/companies/:companyId` you know
 * that `companyId` will always be defined.
 *
 * @example
 * const { companyId } = useRequiredParams({ companyId: "number" });
 */
export function useRequiredParams<
  T extends Record<string, "string" | "number">,
>(options: T) {
  const params = useParams();

  // Validate and cast params based on the provided paramTypes
  const castedParams = Object.entries(options).reduce((acc, [key, type]) => {
    const paramValue = params[key];

    // Check if the param exists
    if (paramValue === undefined || paramValue === null) {
      throw new Error(`Missing required parameter: ${key}`);
    }

    // Attempt to cast the param to the specified type
    acc[key] = type === "number" ? Number(paramValue) : paramValue;

    return acc;
  }, {});

  return castedParams as {
    [K in keyof T]: T[K] extends "number" ? number : string;
  };
}

export function useNavigatonPath() {
  const location = useLocation();
  const params = useRequiredParams({ companyId: "number" });
  const to = (path: string) => `/company/${params.companyId}/${path}`;
  const isActive = (path: string) => location.pathname.includes(to(path));
  return { to, isActive };
}

const parseBaseUrl = (url: string): string | null => {
  const match = url.match(/^(.*?)-/);
  return match ? match[0] : null;
};

const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export function usePathNavigator() {
  const baseUrl = parseBaseUrl(window.location.host);
  const withSubUrl = (path: string): string => {
    if (!baseUrl || !isValidURL(path)) {
      return path;
    }
    return constructNewUrl(path, baseUrl);
  };
  return { withSubUrl };
}

function constructNewUrl(path: string, baseUrl: string | null) {
  const url = new URL(path);
  const [hostnameParts, ...rest] = url.hostname.split(".");
  const updatedHost = `${baseUrl}${hostnameParts}`;
  url.hostname = [updatedHost, ...rest].join(".");
  if (url.toString().includes(window.location.host)) {
    return "/";
  }
  return url.toString();
}

/**
 * Get the key to store persisted search params in session storage.
 *
 * NOTE: the number in the key is the version of the persisted search params.
 * It allows us to invalidate old persisted search params when the structure
 * of the persisted params data changes.
 */
const getPersistKey = (key: string) => `search-params:v1:${key}`;

type Location = { path: string; params: string };
/**
 * An utility hook to persist a path and its params in session storage
 * NOTE: This persists the path without the company/:companyId/ prefix
 * because our navigation components want the path without the prefix
 * and the main usage for this hook is for navigation
 */
export function useLocationStore() {
  const { companyId } = useRequiredParams({ companyId: "number" });

  const setLocation = (key: string) => {
    const subpath = window.location.pathname.split(`company/${companyId}/`)[1];
    sessionStorage.setItem(
      key,
      JSON.stringify({
        path: subpath,
        params: window.location.search,
      }),
    );
  };

  const getLocation = (key: string, removeKey: boolean = false) => {
    const item = sessionStorage.getItem(key);
    if (removeKey) {
      sessionStorage.removeItem(key);
    }
    return item ? (JSON.parse(item) as Location) : undefined;
  };

  return { setLocation, getLocation };
}
/**
 * The key to store a search param that is persisted across every route.
 * Scoped by `companyId` so switching companies never leaks a value, eg. a
 * selected tree node, from one company into another.
 */
const getGlobalPersistKey = (companyId: string, param: string) =>
  `search-params:v1:global:${companyId}:${param}`;

/**
 * Shape for a route's `handle` property to opt out of global search params
 * (the ones passed to `usePersistSearchParams`). Set to `true` to exclude all
 * of them on that route, or an array to exclude only specific param names.
 *
 * @example
 * { path: "history", handle: { excludeGlobalSearchParams: true }, ... }
 */
export type RouteHandle = {
  excludeGlobalSearchParams?: boolean | string[];
};

/**
 * Filters out any global search params that the currently matched routes
 * opted out of via their `handle: { excludeGlobalSearchParams }` property.
 */
function useFilteredGlobalSearchParams(globalParams: string[]) {
  const matches = useMatches();

  const excludedParams = matches.flatMap((match) => {
    const { excludeGlobalSearchParams } = (match.handle ?? {}) as RouteHandle;

    if (!excludeGlobalSearchParams) {
      return [];
    }

    return excludeGlobalSearchParams === true
      ? globalParams
      : excludeGlobalSearchParams;
  });

  return excludedParams.length
    ? globalParams.filter((param) => !excludedParams.includes(param))
    : globalParams;
}

/**
 * A utility hook to persist search params in session storage for all pages.
 *
 * NOTE: this hook does not handle the restoration of search params!
 * For that use the `restoreSearchParamsLoader` function and pass it to the
 * routes where you want to restore search params.
 *
 * @param allGlobalParams names of search params that should be carried over
 * between *any* routes (not just restored on the same pathname). When one of
 * these params is present in the URL it is remembered, and on any page that
 * is missing it, it gets restored from the last remembered value. A route can
 * opt out via its `handle`, see `RouteHandle`.
 */
export function usePersistSearchParams(allGlobalParams: string[] = []) {
  const location = useLocation();
  const persistKey = getPersistKey(location.pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const globalParams = useFilteredGlobalSearchParams(allGlobalParams);
  const { companyId } = useRequiredParams({ companyId: "string" });
  // Tracks the previous pathname so we only restore a missing global param
  // when navigating to a new page, not when it's removed on the current one.
  const previousPathnameRef = useRef<string | null>(null);

  // Keep search params in sync with session storage, excluding global params
  // since those are persisted separately and shouldn't be tied to this
  // pathname (otherwise they'd leak back in via `restoreSearchParamsLoader`
  // even after being cleared from the global store elsewhere).
  useEffect(() => {
    const localSearchParams = new URLSearchParams(searchParams);
    for (const param of globalParams) {
      localSearchParams.delete(param);
    }

    if (localSearchParams.size === 0) {
      sessionStorage.removeItem(persistKey);
    } else {
      sessionStorage.setItem(persistKey, localSearchParams.toString());
    }
  }, [searchParams, persistKey, globalParams]);

  // Remember and restore params that should persist across all routes
  useEffect(() => {
    const isNewPageNavigation =
      previousPathnameRef.current !== location.pathname;
    previousPathnameRef.current = location.pathname;

    const nextSearchParams = new URLSearchParams(searchParams);
    let hasRestoredParam = false;

    for (const param of globalParams) {
      const value = searchParams.get(param);
      if (value) {
        sessionStorage.setItem(getGlobalPersistKey(companyId, param), value);
        continue;
      }

      // The param is missing on the same page the user is already on, which
      // means it was intentionally removed, eg. via some UI control. Respect
      // that instead of restoring it, and forget it so it stays gone.
      if (!isNewPageNavigation) {
        sessionStorage.removeItem(getGlobalPersistKey(companyId, param));
        continue;
      }

      const persistedValue = sessionStorage.getItem(
        getGlobalPersistKey(companyId, param),
      );
      if (persistedValue) {
        nextSearchParams.set(param, persistedValue);
        hasRestoredParam = true;
      }
    }

    if (hasRestoredParam) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    searchParams,
    globalParams,
    setSearchParams,
    location.pathname,
    companyId,
  ]);
}

/**
 * A loader function for React Router routes to restore persisted search params
 * from session storage for given route. This ensures that the search params
 * are restored before the route component is rendered so if any of the components
 * within the route access the search params they will be up to date.
 */
export function restoreSearchParamsLoader({
  request,
  defaultParams,
}: LoaderFunctionArgs & { defaultParams?: string }) {
  const url = new URL(request.url);
  const isPageNavigation = url.pathname !== window.location.pathname;

  /**
   * Only restore search params if there are no search params in the URL and
   * we are navigating to a new page instead of eg. updating the search params
   * in the current page (which causes a navigation -> which triggers this loader).
   *
   * The search params in the URL have higher priority than the persisted ones.
   */
  if (isPageNavigation && url.searchParams.size === 0) {
    const persistKey = getPersistKey(url.pathname);
    const persistedParams = sessionStorage.getItem(persistKey) ?? defaultParams;

    /**
     * NOTE: `URLSearchParams` doesn't provide a way to check if the string
     * is a valid query string so we do some basic validation here.
     */
    if (persistedParams && persistedParams.includes("=")) {
      url.search = persistedParams;
      return redirect(`${url.pathname}${url.search}`);
    }
  }

  // React Router loaders need to return some value
  return null;
}

const FAST_POLL_INTERVAL_MS = 3000;
const SLOW_POLL_INTERVAL_MS = 60000;
const FAST_POLL_WINDOW_MS = 60000;

function getUserModifiedTimeStamps(points: PointBase[]): number[] {
  return (
    points
      .filter((point) => point.isPending)
      // used ! (Non-null Assertion Operator) because
      // isPending can only be true when userModifiedAt exists
      .map((point) => point.userModifiedAt!.toMillis())
  );
}

/**
 * Polls every 3s for the first 60s, then every minute.
 * Stops when there are no pending updates.
 */
export function dynamicRefetchInterval(points: PointBase[]): number | false {
  const userModifiedTimeStamps = getUserModifiedTimeStamps(points);

  if (!userModifiedTimeStamps.length) {
    // stop polling
    return false;
  }

  const latestTimeStamp = Math.max(...userModifiedTimeStamps);
  const timeSincePending = Date.now() - latestTimeStamp;

  return timeSincePending > FAST_POLL_WINDOW_MS
    ? SLOW_POLL_INTERVAL_MS
    : FAST_POLL_INTERVAL_MS;
}
